import * as React from "react";

import { useAuditIdentityOptions } from "@/shared/components/principal";
import {
  resourceIdFilter,
  tagFilter,
  type FilterDefinition,
} from "@/shared/components/ResourceView";

/**
 * The Sessions rows whose definition needs nothing from React — everything
 * except the two audit-principal filters, which have to resolve org-scoped
 * directories at render time. Split out from {@link coreSessionFilters} so
 * {@link useCoreSessionFilters} can slot the identity rows into picker order
 * (audit fields before tags) instead of appending them after it.
 */
const sessionScalarFilters = [
  {
    type: "string",
    field: "id",
    name: "Session ID",
    ...resourceIdFilter,
  },
  { type: "string", field: "name", name: "Name" },
  { type: "date", field: "start_time", name: "Start time" },
  { type: "date", field: "end_time", name: "End time" },
  { type: "numeric", field: "duration", name: "Duration (ns)" },
] as const satisfies readonly FilterDefinition[];

/**
 * Free-text input, with no `options` resolver because the backend does not
 * expose a Sessions tag autocomplete endpoint. Datasets/Events/Files populate
 * their tag picker via `getTagsForOrg`; until Sessions ships the equivalent,
 * users type tag values directly.
 */
const sessionTagsFilter = {
  type: "string",
  field: "tags",
  name: "Tags",
  ...tagFilter,
} as const satisfies FilterDefinition;

/**
 * The statically-definable Sessions filter rows. Each `field` is the roboQL
 * token (alias or canonical label) the backend route forwards to
 * `SessionPredicateGenerator`, which is what `coreSessionFilters.test.ts`
 * pins against the generated catalog.
 *
 * Mounts do NOT consume this directly — they call
 * {@link useCoreSessionFilters}, which returns these rows plus the audit
 * identity filters. It stays exported as the catalog-checkable row set, since
 * a hook's return value can't be walked outside a render.
 */
export const coreSessionFilters = [
  ...sessionScalarFilters,
  sessionTagsFilter,
] as const satisfies readonly FilterDefinition[];

/**
 * Filterable Sessions fields common to every Sessions list mount. Mounts
 * compose their picker by spreading the array this hook returns and appending
 * the cross-aggregate id rows their scope admits: {@link
 * sessionDatasetIdFilter}, {@link sessionDeviceIdFilter}, or both.
 *
 * A hook (not a static const) because the `created_by` / `modified_by`
 * identity filters need org-scoped directories — see
 * {@link useAuditIdentityOptions}. Sessions store the audit principal in the
 * same `<kind>:<id>` form as datasets/events, so both are `identity` filters
 * over the same kinds.
 */
export function useCoreSessionFilters(
  orgId: string | undefined,
): FilterDefinition[] {
  const identityOptions = useAuditIdentityOptions(orgId);

  return React.useMemo(
    () => [
      ...sessionScalarFilters,
      {
        type: "identity",
        field: "created_by",
        name: "Created By",
        options: identityOptions,
      },
      {
        type: "identity",
        field: "modified_by",
        name: "Modified By",
        options: identityOptions,
      },
      sessionTagsFilter,
    ],
    [identityOptions],
  );
}

/**
 * The `dataset.dataset_id` row of the Sessions filter picker: narrows to
 * sessions holding at least one file from a given dataset. Appended by every
 * Sessions mount whose scope leaves the dataset axis free (the top-level
 * `/sessions` page, the device-scoped tab, and a collection's Sessions tab);
 * the dataset-scoped tab omits it because that axis is already pinned
 * server-side. Shared rather than repeated per mount so the label and the
 * roboQL field token cannot drift apart between pickers.
 */
export const sessionDatasetIdFilter = {
  type: "string",
  field: "dataset.dataset_id",
  name: "Dataset ID",
  ...resourceIdFilter,
} as const satisfies FilterDefinition;

/**
 * The `device.device_id` row of the Sessions filter picker: narrows to
 * sessions attached to a given device. Appended by every Sessions mount whose
 * scope leaves the device axis free (the top-level `/sessions` page, the
 * dataset-scoped tab, and a collection's Sessions tab); the device-scoped tab
 * omits it because that axis is already pinned server-side. Shared rather than
 * repeated per mount so the label and the roboQL field token cannot drift
 * apart between pickers.
 */
export const sessionDeviceIdFilter = {
  type: "string",
  field: "device.device_id",
  name: "Device ID",
  ...resourceIdFilter,
} as const satisfies FilterDefinition;
